import type { Organization, OrganizationAccess, OrganizationMember } from '@hotjar/organization';
import type { CurrentUser } from '@hotjar/user';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { noop } from '@hotjar/webapp/lib/function';
import { Avatar, IconButton, Select, Table, Text } from '@hotjar/hotsauce';

import { TRANSLATION_DOMAIN } from '../constants';
import * as S from '../styles';
import { isValidOrganizationAccess } from '../utils/isValidOrganizationAccess';
import type { OrganizationListItem } from '../types';

interface Props {
  currentUser: CurrentUser | undefined;
  organization: OrganizationListItem;
  user: OrganizationMember;
  onAccessLevelChange: (
    organization: Organization,
    user: OrganizationMember,
    newAccessLevel: OrganizationAccess,
  ) => unknown;
  onRevokeAccessClick: (organization: Organization, user: OrganizationMember) => unknown;
}

export function TeamListUserRow({
  currentUser,
  organization,
  user,
  onAccessLevelChange,
  onRevokeAccessClick,
}: Props): JSX.Element {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);

  const isAccountOwner = organization.accountAdminIds.includes(user.id);
  const isCurrentUserAdmin = organization.access === 'admin';
  const isCurrentUser = user.id === currentUser?.id;

  return (
    <Table.Row key={user.id} data-testid={`team-list-user-row-${user.id}`}>
      <S.TableCellVerticalCenter>
        <S.FlexContainerCenter>
          <Avatar userName={user.name} size="l" src={user.avatar ?? undefined} />
          <Text>{user.name}</Text>
        </S.FlexContainerCenter>
      </S.TableCellVerticalCenter>
      <S.TableCellVerticalCenter>{user.email}</S.TableCellVerticalCenter>
      <S.TableCellVerticalCenter>
        {isAccountOwner ? <Text>{t('account-owner')}</Text> : null}

        {(!isAccountOwner && !isCurrentUserAdmin) || (!isAccountOwner && isCurrentUser) ? (
          <Text>{t(`access-level.${user.access}`)}</Text>
        ) : null}

        {!isAccountOwner && isCurrentUserAdmin && !isCurrentUser ? (
          <Select
            defaultValue={user.access}
            isDisabled={user.id === currentUser?.id}
            onChange={(event) =>
              isValidOrganizationAccess(event.currentTarget.value)
                ? onAccessLevelChange(organization, user, event.currentTarget.value)
                : noop
            }
          >
            <option value="read">{t('access-level.read')}</option>
            <option value="write">{t('access-level.write')}</option>
            <option value="admin">{t('access-level.admin')}</option>
          </Select>
        ) : null}
      </S.TableCellVerticalCenter>
      <S.TableCellRightAligned>
        {!isAccountOwner && isCurrentUserAdmin && !isCurrentUser ? (
          <IconButton
            icon="close"
            kind="ghost"
            size="s"
            tooltipText={t('revoke-access')}
            description={t('revoke-access')}
            onClick={() => onRevokeAccessClick(organization, user)}
          />
        ) : null}
      </S.TableCellRightAligned>
    </Table.Row>
  );
}
